import type { UploadFile, UploadProps } from 'antd'
import { message, Upload } from 'antd'
import React, { useState } from 'react'

import { NDE_TYPES } from './ndeDataSection.constants'
import {
  Button,
  DataItemContainer,
  Field,
  Label,
  Select,
  SubHeader,
} from './ndeDataSection.styles'
import { parseNDECsv } from './parseNDECsv'

interface Props {
  onSubmit: (dataType: string, data: number[][]) => Promise<void>
}

export const NDEUploadForm: React.FC<Props> = ({ onSubmit }) => {
  const [selectedType, setSelectedType] = useState('')
  const [file, setFile] = useState<UploadFile>()
  const [parsedData, setParsedData] = useState<number[][]>()

  const uploadProps: UploadProps = {
    accept: '.csv',
    onRemove: () => {
      setFile(undefined)
      setParsedData(undefined)
    },
    beforeUpload: (newFile: UploadFile) => {
      setFile(newFile)
      const reader = new FileReader()

      reader.onload = (e) => {
        if (!e.target?.result) {
          message.error('Failed to read file')
          return
        }

        const result = parseNDECsv(e.target.result.toString())

        if (result.error) {
          message.error(result.error)
          setFile(undefined)
          setParsedData(undefined)
          return
        }

        setParsedData(result.data)
        message.success(
          `Parsed ${result.data.length} × ${result.data[0]?.length || 0}`
        )
      }

      reader.readAsText(newFile as unknown as Blob)
      return false
    },
    fileList: file ? [file] : [],
    maxCount: 1,
    showUploadList: false,
  }

  const handleSubmit = async () => {
    if (!parsedData || !selectedType) {
      message.warning('Please select type and upload CSV')
      return
    }

    await onSubmit(selectedType.toLowerCase(), parsedData)

    // Reset form
    setFile(undefined)
    setParsedData(undefined)
    setSelectedType('')
  }

  return (
    <>
      <SubHeader>Upload New NDE Data</SubHeader>

      <Field>
        <Label>Data Type</Label>
        <Select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}>
          <option value=''>Select type...</option>
          {NDE_TYPES.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </Select>
      </Field>

      <Field>
        <Label>Upload CSV</Label>
        <Upload {...uploadProps}>
          <Button>{file ? file.name : 'Choose CSV File'}</Button>
        </Upload>
      </Field>

      {parsedData && (
        <Field>
          <DataItemContainer>
            <span>Preview:</span>
            <span style={{ color: 'rgba(255,255,255,0.6)' }}>
              {parsedData.length} × {parsedData[0]?.length || 0}
            </span>
          </DataItemContainer>
        </Field>
      )}

      <Button
        $variant='primary'
        onClick={handleSubmit}
        disabled={!parsedData || !selectedType}>
        Submit NDE Data
      </Button>
    </>
  )
}
